import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Download,
  Trash2,
  Pencil,
  Copy,
  MessageCircle,
  QrCode,
  Eye,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  listCampaigns,
  createCampaign,
  deleteCampaign,
  downloadKit,
  getConfig,
  updateConfig,
} from "@/api/client";
import { AdminShell } from "@/components/admin/AdminShell";

const Panel = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 backdrop-blur-sm ${className}`}
  >
    {children}
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-amber-300/10 bg-[#080b16]/50 px-3 py-2 text-center">
    <div data-testid={testId} className="text-lg font-semibold text-amber-200">
      {value}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-amber-100/40">
      {label}
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    whatsapp_number: "",
    public_base_url: "",
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_secret_set: false,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [creating, setCreating] = useState(false);
  const [kitBusy, setKitBusy] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, cfg] = await Promise.all([listCampaigns(), getConfig()]);
      setCampaigns(c);
      setConfig(cfg);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const updated = await updateConfig(config);
      setConfig(updated);
      toast.success("Global settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const created = await createCampaign({
        temple_name: newName.trim(),
        slug: newSlug.trim() || undefined,
      });
      setNewName("");
      setNewSlug("");
      toast.success(`Created “${created.temple_name}”`);
      await refresh();
    } catch {
      toast.error("Could not create campaign");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete “${c.temple_name}”? This cannot be undone.`)) return;
    try {
      await deleteCampaign(c.id);
      toast.success("Campaign deleted");
      setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
    } catch {
      toast.error("Could not delete");
    }
  };

  const handleKit = async (c) => {
    setKitBusy(c.id);
    try {
      await downloadKit(c.id, c.slug);
      toast.success("Campaign Kit downloaded");
    } catch {
      toast.error("Could not download kit");
    } finally {
      setKitBusy(null);
    }
  };

  return (
    <AdminShell
      title="Campaigns"
      subtitle="Each campaign is its own standalone website at takekrishnahome.com/{slug} — a festival or temple experience (Janmashtami, Radhastami, Kartik Yatra…) with its own Story Builder, CTAs, QR kit and analytics."
    >
      {/* Global settings */}
      <Panel className="mb-8 p-6">
        <div className="mb-4 flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-amber-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
            Global WhatsApp & Site
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">
              WhatsApp Business Number (digits only)
            </label>
            <input
              data-testid="config-whatsapp-input"
              value={config.whatsapp_number}
              onChange={(e) =>
                setConfig((p) => ({ ...p, whatsapp_number: e.target.value }))
              }
              className="w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-2.5 text-sm outline-none focus:border-amber-300/50"
              placeholder="917208920560"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">
              Public Base URL
            </label>
            <input
              data-testid="config-baseurl-input"
              value={config.public_base_url}
              onChange={(e) =>
                setConfig((p) => ({ ...p, public_base_url: e.target.value }))
              }
              className="w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-2.5 text-sm outline-none focus:border-amber-300/50"
              placeholder="https://takekrishnahome.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">
              Razorpay Key ID
            </label>
            <input
              data-testid="config-razorpay-key-input"
              value={config.razorpay_key_id || ""}
              onChange={(e) =>
                setConfig((p) => ({ ...p, razorpay_key_id: e.target.value }))
              }
              className="w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-2.5 text-sm outline-none focus:border-amber-300/50"
              placeholder="rzp_test_xxxxxxxx"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">
              Razorpay Key Secret{" "}
              {config.razorpay_secret_set && (
                <span className="text-amber-300/70">· saved</span>
              )}
            </label>
            <input
              data-testid="config-razorpay-secret-input"
              type="password"
              value={config.razorpay_key_secret || ""}
              onChange={(e) =>
                setConfig((p) => ({ ...p, razorpay_key_secret: e.target.value }))
              }
              className="w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-2.5 text-sm outline-none focus:border-amber-300/50"
              placeholder={
                config.razorpay_secret_set ? "•••••••• (leave blank to keep)" : "Enter key secret"
              }
            />
          </div>
        </div>
        <p className="mt-3 text-xs text-amber-100/40">
          Razorpay keys power the “Sponsor a Bhagavatam” donations (UPI, cards & net-banking).
          The secret is stored securely and never shown again.
        </p>
        <button
          data-testid="config-save-btn"
          onClick={handleSaveConfig}
          disabled={savingConfig}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 text-xs font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
        >
          {savingConfig ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Save settings
        </button>
      </Panel>

      {/* Create */}
      <Panel className="mb-8 p-6">
        <div className="mb-1 flex items-center gap-2">
          <Plus className="h-4 w-4 text-amber-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
            New Campaign
          </h2>
        </div>
        <p className="mb-4 text-xs text-amber-100/40">
          Creates a brand-new microsite (its own website) at
          takekrishnahome.com/{"{slug}"}. Name it after the festival or temple —
          then open it to build its story and CTAs.
        </p>
        <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1.5 block text-xs text-amber-100/50">
              Campaign name
            </label>
            <input
              data-testid="create-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-2.5 text-sm outline-none focus:border-amber-300/50"
              placeholder="Janmashtami · Radhastami · Kartik Yatra"
            />
          </div>
          <div className="min-w-[180px] flex-1">
            <label className="mb-1.5 block text-xs text-amber-100/50">
              URL slug (optional — auto-generated)
            </label>
            <input
              data-testid="create-slug-input"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              className="w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-2.5 text-sm outline-none focus:border-amber-300/50"
              placeholder="janmashtami"
            />
          </div>
          <button
            data-testid="create-campaign-btn"
            type="submit"
            disabled={creating}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Create
          </button>
        </form>
      </Panel>

      {/* List */}
      {loading ? (
        <div className="grid place-items-center py-20 text-amber-100/40">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <p className="py-16 text-center text-amber-100/40">
          No campaigns yet. Create your first festival or temple website above.
        </p>
      ) : (
        <div className="grid gap-5 md:grid-cols-2" data-testid="campaigns-list">
          {campaigns.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`campaign-card-${c.slug}`}
            >
              <Panel className="h-full p-6">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-100">
                      {c.temple_name}
                    </h3>
                    <span className="mt-1 inline-block rounded-full border border-amber-300/20 bg-amber-400/5 px-2.5 py-0.5 font-mono text-xs text-amber-300/80">
                      /{c.slug}
                    </span>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider ${
                      c.active
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-white/5 text-amber-100/40"
                    }`}
                  >
                    {c.active ? "Active" : "Off"}
                  </span>
                </div>

                <div className="mb-4 space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-amber-100/60">
                    <QrCode className="h-3.5 w-3.5 shrink-0 text-amber-300/70" />
                    <span className="truncate">{c.site_url}</span>
                    <button
                      onClick={() => copy(c.site_url)}
                      className="ml-auto text-amber-300/60 hover:text-amber-300"
                      title="Copy URL"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-amber-100/60">
                    <MessageCircle className="h-3.5 w-3.5 shrink-0 text-amber-300/70" />
                    <span className="truncate">{c.whatsapp_link}</span>
                    <button
                      onClick={() => copy(c.whatsapp_link)}
                      className="ml-auto text-amber-300/60 hover:text-amber-300"
                      title="Copy WhatsApp link"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-3 gap-2">
                  <Stat
                    label="Visits"
                    value={c.analytics?.website_visits ?? 0}
                    testId={`stat-visits-${c.slug}`}
                  />
                  <Stat
                    label="Tokens"
                    value={c.analytics?.tokens_issued ?? 0}
                    testId={`stat-tokens-${c.slug}`}
                  />
                  <Stat
                    label="CTA clicks"
                    value={c.analytics?.cta_clicks ?? 0}
                    testId={`stat-clicks-${c.slug}`}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`edit-btn-${c.slug}`}
                    onClick={() => navigate(`/admin/campaigns/${c.id}`)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-1.5 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit & CTAs
                  </button>
                  <button
                    data-testid={`kit-btn-${c.slug}`}
                    onClick={() => handleKit(c)}
                    disabled={kitBusy === c.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
                  >
                    {kitBusy === c.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Campaign Kit
                  </button>
                  <a
                    href={`/${c.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-1.5 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10"
                  >
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </a>
                  <button
                    data-testid={`delete-btn-${c.slug}`}
                    onClick={() => handleDelete(c)}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-red-400/20 px-3 py-1.5 text-xs font-medium text-red-300/80 transition-all hover:border-red-400/50 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </Panel>
            </motion.div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
